// DashboardDetail.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import axios from './axiosConfig';
import PanelDisplay from './PanelDisplay';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);
dayjs.extend(customParseFormat);

// ====== Types ======
type PanelItem = {
  id?: number;
  dashboard_id?: number;

  order: number;
  type: string;
  data?: any;

  width?: number;
  height?: number;
  position_x?: number;
  position_y?: number;
  i?: string;

  source?: string;
  destination?: string;
};

const stringifyPanelData = (data: any): string => {
  if (data === null || data === undefined) return '{}';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return '{}';
  }
};

// ===== Helpers =====
const PROM_FETCH_TYPES = new Set<string>([
  'GaugeChart',
  'SolidColorCard',
  'TimeSeriesChart',
  'BandwidthChart',
]);

const parseValueWithUnit = (v: string) => {
  const trimmed = (v || '').trim();
  if (!trimmed) return { num: NaN, unit: '' };

  const match = trimmed.match(/-?\d+(\.\d+)?/);
  if (!match) return { num: NaN, unit: trimmed };

  const num = parseFloat(match[0]);
  const unit = trimmed.replace(match[0], '').trim();
  return { num, unit };
};

const buildSolidColorCardData = (title: string, text: string, base: any = {}) => {
  const normalized = (text || '').toLowerCase();

  const safeKeywords = ['up', 'established', 'idle', 'ok', 'healthy', 'running'];
  const unsafeKeywords = ['down', 'critical', 'failed', 'error'];

  const isSafe =
    safeKeywords.some((k) => normalized === k || normalized.includes(k)) &&
    !unsafeKeywords.some((k) => normalized.includes(k));

  const safeColors = {
    textColor: '#FFFFFF',
    subTextColor: '#DFF5E1',
    backgroundColor: '#1F8B4C',
  };

  const unsafeColors = {
    textColor: '#FFFFFF',
    subTextColor: '#F6D6D6',
    backgroundColor: '#B02A37',
  };

  const colors = isSafe ? safeColors : unsafeColors;

  return {
    ...base,
    title,
    text,
    subText: '',
    textColor: colors.textColor,
    subTextColor: colors.subTextColor,
    backgroundColor: colors.backgroundColor,
  };
};

const safeParsePanelData = (raw: any) => {
  if (raw === null || raw === undefined) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw;
  return {};
};

const normalizePanels = (list: PanelItem[]) => {
  const safe = Array.isArray(list) ? list : [];

  return safe.map((p, idx) => {
    const id = Number.isFinite(Number(p.id)) ? Number(p.id) : -(idx + 1);

    const w = Number.isFinite(Number(p.width)) ? Math.round(Number(p.width)) : 6;
    const h = Number.isFinite(Number(p.height)) ? Math.round(Number(p.height)) : 6;
    const x = Number.isFinite(Number(p.position_x)) ? Math.round(Number(p.position_x)) : 0;
    const y = Number.isFinite(Number(p.position_y)) ? Math.round(Number(p.position_y)) : 0;

    return {
      ...p,
      id,
      i: String(id),
      width: w,
      height: h,
      position_x: x,
      position_y: y,
      order: Number.isFinite(Number(p.order)) ? Number(p.order) : idx,
    };
  });
};

const mergePanelsPreserveSelfFetch = (prev: PanelItem[], next: PanelItem[]) => {
  const nextById = new Map<number, PanelItem>();
  next.forEach((p) => {
    const id = Number(p.id);
    if (Number.isFinite(id)) nextById.set(id, p);
  });

  return prev.map((p) => {
    const id = Number(p.id);
    const n = nextById.get(id);
    if (!n) return p;

    if (PROM_FETCH_TYPES.has(p.type)) {
      return { ...p, data: n.data };
    }

    return p;
  });
};

// -----------------------------
// Timezone helpers for DatePicker
// -----------------------------
const TZ_WALL_FMT = 'YYYY-MM-DD HH:mm:ss';

function ensureTz(tz?: string) {
  const s = String(tz || '').trim();
  return s ? s : 'UTC';
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

/**
 * Convert stored UTC ISO -> a "fake local Date" whose local wall-clock equals the desired timezone wall-clock.
 * This allows react-datepicker (which always renders in browser local timezone) to show the unified TZ time.
 */
function utcIsoToPickerDate(utcIso: string, tz: string): Date {
  const zone = ensureTz(tz);
  const d = dayjs.utc(utcIso).tz(zone);

  // Construct a local Date with the same wall-clock parts (year/month/day/hour/min/sec) as TZ time.
  return new Date(
    d.year(),
    d.month(),
    d.date(),
    d.hour(),
    d.minute(),
    d.second(),
    d.millisecond(),
  );
}

/**
 * Convert a Date picked in browser local wall-clock -> interpret those wall-clock parts as timezone wall-clock,
 * then convert to UTC ISO for storage/request.
 */
function pickerDateToUtcIso(picked: Date, tz: string): string {
  const zone = ensureTz(tz);

  const s =
    `${picked.getFullYear()}-${pad2(picked.getMonth() + 1)}-${pad2(picked.getDate())} ` +
    `${pad2(picked.getHours())}:${pad2(picked.getMinutes())}:${pad2(picked.getSeconds())}`;

  return dayjs.tz(s, TZ_WALL_FMT, zone).utc().toISOString();
}

const DashboardDetail: React.FC = () => {
  const location = useLocation();

  const initialPanelsRaw: PanelItem[] = location.state?.dashboardData?.panels || [];
  const routeDashboardName: string =
    location.state?.dashboardName ||
    decodeURIComponent(window.location.pathname.split('/').pop() || '');

  const [dashboardName] = useState<string>(routeDashboardName);

  const [panels, setPanels] = useState<PanelItem[]>(normalizePanels(initialPanelsRaw));
  const panelsRef = useRef<PanelItem[]>(panels);

  useEffect(() => {
    panelsRef.current = panels;
  }, [panels]);

  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);

  const supportedTimeSeriesTypes = ['TimeSeriesChart', 'BandwidthChart'];

  const hasTimeSeriesPanel = useMemo(
    () => panels.some((p) => supportedTimeSeriesTypes.includes(p.type)),
    [panels],
  );

  const hasPromFetchPanel = useMemo(
    () => panels.some((p) => PROM_FETCH_TYPES.has(p.type)),
    [panels],
  );

  // -----------------------------
  // Effective timezone (from backend)
  // -----------------------------
  const [timezone, setTimezone] = useState<string>('UTC');
  const [timezoneSource, setTimezoneSource] = useState<string>('system');

  useEffect(() => {
    const loadTimezone = async () => {
      try {
        const resp = await axios.get('/api/settings/timezone');
        const tz = String(resp?.data?.timezone || '').trim() || 'UTC';
        const src = String(resp?.data?.source || '').trim() || 'system';
        setTimezone(tz);
        setTimezoneSource(src);
      } catch {
        setTimezone('UTC');
        setTimezoneSource('system');
      }
    };
    loadTimezone();
  }, []);

  // -----------------------------
  // Store time range internally as UTC ISO
  // -----------------------------
  const [startUtcIso, setStartUtcIso] = useState<string>(() => {
    return new Date(Date.now() - 60 * 60 * 1000).toISOString();
  });
  const [endUtcIso, setEndUtcIso] = useState<string>(() => {
    return new Date().toISOString();
  });

  const [isCustomTimeSelected, setIsCustomTimeSelected] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // DatePicker selected values (fake local date to show unified tz wall-clock)
  const startPickerDate = useMemo(() => utcIsoToPickerDate(startUtcIso, timezone), [startUtcIso, timezone]);
  const endPickerDate = useMemo(() => utcIsoToPickerDate(endUtcIso, timezone), [endUtcIso, timezone]);

  const maxPickerDate = useMemo(() => utcIsoToPickerDate(new Date().toISOString(), timezone), [timezone]);

  // ======= Init fetch when state didn't bring panels =======
  useEffect(() => {
    if (initialPanelsRaw && initialPanelsRaw.length > 0) return;

    const fetchPanels = async () => {
      try {
        const res = await axios.get(`/api/dashboard/${dashboardName}`);
        setPanels(normalizePanels(res.data.panels || []));
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to load dashboard panels:', e);
        setError('Failed to load dashboard data.');
      }
    };

    fetchPanels();
  }, [dashboardName, initialPanelsRaw]);

  // ======= Fetch dashboard data =======
  const fetchDashboardData = async (sUtcIso: string, eUtcIso: string) => {
    const currentPanels = panelsRef.current;
    if (!currentPanels || currentPanels.length === 0) return;

    try {
      const nextPanels = await Promise.all(
        currentPanels.map(async (panel) => {
          const type = panel.type;

          if (!PROM_FETCH_TYPES.has(type)) {
            return panel;
          }

          const parsedData = safeParsePanelData(panel.data);

          const metricName: string =
            parsedData.metricName ||
            parsedData.metric ||
            parsedData.name ||
            dashboardName;

          const valueQuery: string =
            parsedData.valueQuery ||
            parsedData.query ||
            metricName;

          if (type === 'GaugeChart') {
            try {
              const resp = await axios.get('/api/tenant/metric-current-value', {
                params: { name: metricName },
              });

              const detail = resp.data || {};
              const display =
                (detail.display_value ?? '').toString().trim() ||
                (detail.mapped_value ?? '').toString().trim() ||
                (detail.raw_value ?? '').toString().trim() ||
                '';

              const { num, unit } = parseValueWithUnit(display);

              const gaugeData = {
                ...parsedData,
                title: parsedData.title || metricName,
                value: Number.isNaN(num) ? 0 : num,
                unit,
                isHealthIncreasing: true,
                metricName,
                valueQuery,
              };

              return { ...panel, data: gaugeData };
            } catch (e: any) {
              // eslint-disable-next-line no-console
              console.error('Failed to fetch gauge value for', metricName, e);
              setError(`Failed to fetch gauge value for ${metricName}`);
              return panel;
            }
          }

          if (type === 'SolidColorCard') {
            try {
              const resp = await axios.get('/api/tenant/metric-current-value', {
                params: { name: metricName },
              });

              const detail = resp.data || {};
              const display =
                (detail.display_value ?? '').toString().trim() ||
                (detail.mapped_value ?? '').toString().trim() ||
                (detail.raw_value ?? '').toString().trim() ||
                '';

              const cardData = buildSolidColorCardData(parsedData.title || metricName, display, {
                metricName,
                valueQuery,
              });

              return { ...panel, data: cardData };
            } catch (e: any) {
              // eslint-disable-next-line no-console
              console.error('Failed to fetch card value for', metricName, e);
              setError(`Failed to fetch card value for ${metricName}`);
              return panel;
            }
          }

          if (type === 'TimeSeriesChart' || type === 'BandwidthChart') {
            try {
              const body = {
                query: valueQuery,
                // IMPORTANT: send UTC ISO (not Date object)
                startTime: sUtcIso,
                endTime: eUtcIso,
                step: parsedData.step || '60s',
              };

              const resp = await axios.post('/api/panels/timeseries/promql', body);
              const rows: Array<{ timestamp: string; value: number; unit?: string }> =
                resp.data?.data || [];

              const formatted = rows.map((item) => [
                item.timestamp,
                item.value,
                item.unit || parsedData.unit || '',
              ]);

              const unit = formatted[0]?.[2] || parsedData.unit || '';

              const timeSeriesData = {
                ...parsedData,
                title: parsedData.title || metricName,
                data: formatted,
                unit,
                metricName,
                valueQuery,
              };

              return { ...panel, data: timeSeriesData };
            } catch (e: any) {
              // eslint-disable-next-line no-console
              console.error('Failed to fetch time series for', metricName, e);
              setError(`Failed to fetch time series for ${metricName}`);
              return panel;
            }
          }

          return panel;
        }),
      );

      setPanels((prev) => normalizePanels(mergePanelsPreserveSelfFetch(prev, nextPanels)));
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch dashboard data:', e);
      setError('Failed to fetch dashboard data');
    }
  };

  // ===== Auto refresh time range =====
  const updateTimeRangeAndFetchData = () => {
    const nowUtc = new Date().toISOString();
    const oneHourAgoUtc = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    setStartUtcIso(oneHourAgoUtc);
    setEndUtcIso(nowUtc);
    fetchDashboardData(oneHourAgoUtc, nowUtc);
  };

  useEffect(() => {
    if (!hasPromFetchPanel) return;

    if (!isCustomTimeSelected) {
      updateTimeRangeAndFetchData();
      intervalRef.current = setInterval(updateTimeRangeAndFetchData, 60_000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomTimeSelected, hasPromFetchPanel]);

  const handleDateSubmit = () => {
    const s = dayjs.utc(startUtcIso);
    const e = dayjs.utc(endUtcIso);
    if (s.isValid() && e.isValid() && s.isAfter(e)) {
      setError('Invalid time range: Start time cannot be later than end time.');
      return;
    }

    setIsCustomTimeSelected(true);
    fetchDashboardData(startUtcIso, endUtcIso);
  };

  const handleResetToAutoUpdate = () => {
    setIsCustomTimeSelected(false);
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`Error attempting to exit full-screen mode: ${err.message}`);
      });
    }
  };

  // ===== Save layout =====
  const handleSaveLayout = async () => {
    try {
      const simplifiedPanels = panels.map((panel) => ({
        id: panel.id,
        dashboard_id: panel.dashboard_id,
        type: panel.type,
        data: stringifyPanelData(panel.data),
        width: panel.width,
        height: panel.height,
        position_x: panel.position_x,
        position_y: panel.position_y,
        order: panel.order,
      }));

      // eslint-disable-next-line no-console
      console.log('Save Payload:', JSON.stringify({ panels: simplifiedPanels }, null, 2));

      await axios.post('/api/dashboard/edit-panels', { panels: simplifiedPanels });

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Save Failed:', e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  if (!panels || panels.length === 0) {
    return (
      <Container fluid style={{ paddingTop: 10 }}>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        <p>No panels available to display.</p>
      </Container>
    );
  }

  // ===== Header actions =====
  const extraHeaderActions = (
    <>
      {hasTimeSeriesPanel && (
        <Form className="d-flex align-items-center gap-2 me-2" style={{ whiteSpace: 'nowrap' }}>
          <div className="d-flex flex-column me-1">
            <Form.Label className="mb-1" style={{ fontSize: 11 }}>
              Start
            </Form.Label>
            <DatePicker
              selected={startPickerDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setStartUtcIso(pickerDateToUtcIso(date, timezone));
                setIsCustomTimeSelected(true);
              }}
              dateFormat="yyyy-MM-dd HH:mm:ss"
              showTimeSelect
              maxDate={maxPickerDate}
            />
          </div>

          <div className="d-flex flex-column me-1">
            <Form.Label className="mb-1" style={{ fontSize: 11 }}>
              End
            </Form.Label>
            <DatePicker
              selected={endPickerDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setEndUtcIso(pickerDateToUtcIso(date, timezone));
                setIsCustomTimeSelected(true);
              }}
              dateFormat="yyyy-MM-dd HH:mm:ss"
              showTimeSelect
              maxDate={maxPickerDate}
            />
          </div>

          <div className="d-flex flex-column justify-content-end">
            <div className="d-flex gap-1 mb-1">
              <Button size="sm" variant="outline-primary" onClick={handleDateSubmit}>
                Fetch
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleResetToAutoUpdate}>
                Auto
              </Button>
            </div>
          </div>

          <div className="d-flex flex-column justify-content-end" style={{ marginLeft: 6, opacity: 0.75, fontSize: 12 }}>
            TZ: {timezone} ({timezoneSource})
          </div>
        </Form>
      )}

      {!hasTimeSeriesPanel && hasPromFetchPanel && (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => fetchDashboardData(startUtcIso, endUtcIso)}
          style={{ marginRight: 6 }}
          title="Refresh current values"
        >
          Refresh
        </Button>
      )}

      <Button variant="secondary" size="sm" onClick={toggleFullScreen} style={{ marginRight: 6 }}>
        Toggle Fullscreen
      </Button>

      <Button
        variant="primary"
        size="sm"
        onClick={handleSaveLayout}
        disabled={panels.length === 0}
        style={{ marginRight: 6 }}
      >
        Save Layout
      </Button>
    </>
  );

  return (
    <Container fluid style={{ paddingTop: 10 }}>
      {saveStatus === 'success' && (
        <Alert variant="success" onClose={() => setSaveStatus(null)} dismissible>
          Layout saved successfully!
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert variant="danger" onClose={() => setSaveStatus(null)} dismissible>
          Failed to save layout. Please try again.
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <PanelDisplay
        selectedPanels={panels as any}
        onRemovePanel={(panelId: number) =>
          setPanels((prev) => prev.filter((p) => Number(p.id) !== Number(panelId)))
        }
        onUpdatePanels={(newPanels) => setPanels(normalizePanels(newPanels as any))}
        enableSaveAsDashboard={false}
        headerTitle={dashboardName}
        extraHeaderActions={extraHeaderActions}
        defaultEditMode={false}
        timezone={timezone}
      />
    </Container>
  );
};

export default DashboardDetail;
